import { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useFeatureToggle } from '@bizzabo/feature-toggle-client';
import { AppToolId, IAwareness } from '../../types/appTools';

export const AWARENESS_NOTIFICATION_TIME = 10_000;

export enum AwarenessType {
    NONE,
    DOT,
    TOOLTIP,
    ALL,
}
interface useAwarenessNotificationProps {
    awareness: IAwareness;
    toolId: AppToolId;
}

const useAwarenessNotification = ({ awareness, toolId }: useAwarenessNotificationProps) => {
    const [isToolClicked, setIsToolClicked] = useLocalStorage(`${toolId}IsClicked`, false);

    const isTooltipAwarenessEnabled = useFeatureToggle(awareness?.tooltip?.featureToggleName);
    const isDotAwarenessEnabled = useFeatureToggle(awareness?.dot?.featureToggleName);

    const [awarenessType, setAwarenessType] = useState(AwarenessType.NONE);

    const getAwarenessType = () => {
        if (isTooltipAwarenessEnabled && isDotAwarenessEnabled) {
            return AwarenessType.ALL;
        }
        if (isTooltipAwarenessEnabled) {
            return AwarenessType.TOOLTIP;
        }
        return AwarenessType.DOT;
    };

    useEffect(() => {
        if ((!isTooltipAwarenessEnabled && !isDotAwarenessEnabled) || isToolClicked) {
            return;
        }
        const notificationTimer = setTimeout(() => {
            setAwarenessType(getAwarenessType());
        }, AWARENESS_NOTIFICATION_TIME);
        return () => {
            clearTimeout(notificationTimer);
        };
    }, [isTooltipAwarenessEnabled, isDotAwarenessEnabled, isToolClicked]);

    const awarenessHandle = () => {
        if (isTooltipAwarenessEnabled || isDotAwarenessEnabled) {
            setIsToolClicked(true);
        }
        if (awarenessType !== AwarenessType.NONE) {
            setAwarenessType(AwarenessType.NONE);
        }
    };

    const closeTooltipAwareness = () => {
        setIsToolClicked(true);
        if (awarenessType === AwarenessType.TOOLTIP) {
            setAwarenessType(AwarenessType.NONE);
        } else if (awarenessType === AwarenessType.ALL) {
            setAwarenessType(AwarenessType.DOT);
        }
    };

    return { awarenessType, closeTooltipAwareness, awarenessHandle };
};

export default useAwarenessNotification;
